/**
 * Satellite canvassing map for Direct Mail.
 *
 * Markers are diffed against the previous render by key instead of being torn
 * down and rebuilt: a rep drops pins one click at a time, and recreating every
 * marker on each click would drop the open InfoWindow and flicker a whole
 * neighborhood of houses.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/joy';
import type { MailerPinStatus } from '../../types/directMail';
import { waitForGoogleMaps } from './pinGeocoding';
import { draftPinIcon, statusPinIcon, suppressedPinIcon } from './pinMarkers';

export type PinMapMarkerKind = 'pin' | 'draft' | 'suppressed';

export interface PinMapMarker {
  /** Stable across renders — the marker is reused while this key survives. */
  key: string;
  lat: number;
  lng: number;
  kind: PinMapMarkerKind;
  status?: MailerPinStatus;
  title: string;
  subtitle?: string;
}

export interface PinMapTarget {
  lat: number;
  lng: number;
  zoom?: number;
  /**
   * Frame this many square miles around the point instead of using `zoom`.
   * Fitting an area rather than picking a zoom level keeps the same amount of
   * ground on screen whether the map is a narrow column or a wide desktop pane.
   */
  fitAreaSqMiles?: number;
}

export interface PinMapProps {
  markers: PinMapMarker[];
  selectedKey?: string | null;
  /** Recenters the map whenever a new object identity arrives. */
  target?: PinMapTarget | null;
  /** Changing this value refits the viewport around every marker. */
  fitKey?: string | number | null;
  pinningEnabled?: boolean;
  height?: number | string;
  onMapClick?: (lat: number, lng: number) => void;
  onMarkerClick?: (key: string) => void;
}

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const HOUSE_ZOOM = 19;
const MAX_FIT_ZOOM = 19;
const MILES_PER_DEGREE_LAT = 69;

const KEY_REJECTED_MESSAGE =
  'Google rejected the Maps API key for this site. Check VITE_GOOGLE_MAPS_API_KEY and its referrer restrictions.';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function iconFor(marker: PinMapMarker, selected: boolean): google.maps.Icon {
  if (marker.kind === 'suppressed') return suppressedPinIcon(selected);
  if (marker.kind === 'draft') return draftPinIcon(selected);
  return statusPinIcon(marker.status ?? 'pinned', selected);
}

export const PinMap: React.FC<PinMapProps> = ({
  markers,
  selectedKey = null,
  target = null,
  fitKey = null,
  pinningEnabled = true,
  height = 600,
  onMapClick,
  onMarkerClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const markersRef = useRef<Map<string, { marker: google.maps.Marker; signature: string }>>(
    new Map()
  );

  const mapClickRef = useRef(onMapClick);
  const markerClickRef = useRef(onMarkerClick);
  const pinningRef = useRef(pinningEnabled);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    mapClickRef.current = onMapClick;
    markerClickRef.current = onMarkerClick;
    pinningRef.current = pinningEnabled;
  }, [onMapClick, onMarkerClick, pinningEnabled]);

  // Google calls this global when the key itself is bad, which is otherwise
  // indistinguishable from a slow load — the map just never appears.
  useEffect(() => {
    const globalScope = window as Window & { gm_authFailure?: () => void };
    const previous = globalScope.gm_authFailure;
    globalScope.gm_authFailure = () => {
      setError(KEY_REJECTED_MESSAGE);
      previous?.();
    };
    return () => {
      globalScope.gm_authFailure = previous;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initialize = () => {
      if (cancelled || mapRef.current || !containerRef.current) return;
      const maps = window.google.maps;
      const map = new maps.Map(containerRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeId: maps.MapTypeId.SATELLITE,
        // 45° imagery makes rooftops land nowhere near their coordinates.
        tilt: 0,
        // Without this, clicking a business label opens Google's own popup and
        // never reaches the pin handler.
        clickableIcons: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy',
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: maps.ControlPosition.TOP_RIGHT,
          mapTypeIds: [maps.MapTypeId.SATELLITE, maps.MapTypeId.HYBRID, maps.MapTypeId.ROADMAP],
        },
      });
      map.setTilt(0);
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (!pinningRef.current || !event.latLng) return;
        mapClickRef.current?.(event.latLng.lat(), event.latLng.lng());
      });

      mapRef.current = map;
      infoWindowRef.current = new maps.InfoWindow();
      setError(null);
      setReady(true);
    };

    waitForGoogleMaps()
      .then(initialize)
      .catch((loadError: unknown) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : 'Google Maps failed to load.');
      });

    return () => {
      cancelled = true;
    };
  }, [retryToken]);

  useEffect(() => {
    const registry = markersRef.current;
    return () => {
      registry.forEach(({ marker }) => marker.setMap(null));
      registry.clear();
      infoWindowRef.current?.close();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;

    const registry = markersRef.current;
    const seen = new Set<string>();

    markers.forEach((entry) => {
      seen.add(entry.key);
      const selected = entry.key === selectedKey;
      const signature = [
        entry.kind,
        entry.status ?? '',
        selected ? 'on' : 'off',
        entry.lat.toFixed(6),
        entry.lng.toFixed(6),
        entry.title,
      ].join('|');

      const existing = registry.get(entry.key);
      if (existing) {
        if (existing.signature === signature) return;
        existing.marker.setPosition({ lat: entry.lat, lng: entry.lng });
        existing.marker.setIcon(iconFor(entry, selected));
        existing.marker.setTitle(entry.title);
        existing.marker.setZIndex(selected ? 1000 : null);
        existing.signature = signature;
        return;
      }

      const marker = new window.google.maps.Marker({
        position: { lat: entry.lat, lng: entry.lng },
        map,
        title: entry.title,
        icon: iconFor(entry, selected),
        zIndex: selected ? 1000 : null,
      });
      marker.addListener('click', () => markerClickRef.current?.(entry.key));
      registry.set(entry.key, { marker, signature });
    });

    registry.forEach((entry, key) => {
      if (seen.has(key)) return;
      entry.marker.setMap(null);
      registry.delete(key);
    });
  }, [markers, selectedKey, ready]);

  useEffect(() => {
    const map = mapRef.current;
    const infoWindow = infoWindowRef.current;
    if (!ready || !map || !infoWindow) return;

    const entry = selectedKey ? markers.find((item) => item.key === selectedKey) : undefined;
    const marker = selectedKey ? markersRef.current.get(selectedKey)?.marker : undefined;
    if (!entry || !marker) {
      infoWindow.close();
      return;
    }

    infoWindow.setContent(
      `<div style="padding:4px 6px;max-width:240px;color:#111;font-family:sans-serif;font-size:13px;line-height:1.4;">` +
        `<strong>${escapeHtml(entry.title)}</strong>` +
        (entry.subtitle ? `<div style="margin-top:2px;color:#444;">${escapeHtml(entry.subtitle)}</div>` : '') +
        `</div>`
    );
    infoWindow.open({ map, anchor: marker });
  }, [selectedKey, markers, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !target) return;

    if (target.fitAreaSqMiles) {
      const halfSide = Math.sqrt(target.fitAreaSqMiles) / 2;
      const latSpan = halfSide / MILES_PER_DEGREE_LAT;
      const lngSpan = halfSide / (MILES_PER_DEGREE_LAT * Math.cos((target.lat * Math.PI) / 180));
      map.fitBounds(
        new google.maps.LatLngBounds(
          { lat: target.lat - latSpan, lng: target.lng - lngSpan },
          { lat: target.lat + latSpan, lng: target.lng + lngSpan }
        ),
        0
      );
      return;
    }

    map.panTo({ lat: target.lat, lng: target.lng });
    map.setZoom(target.zoom ?? HOUSE_ZOOM);
  }, [target, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || fitKey === null || fitKey === undefined) return;
    const positioned = markers.filter((entry) => entry.kind === 'pin');
    if (positioned.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    positioned.forEach((entry) => bounds.extend({ lat: entry.lat, lng: entry.lng }));
    map.fitBounds(bounds, 48);

    const listener = window.google.maps.event.addListener(map, 'idle', () => {
      const zoom = map.getZoom();
      if (zoom !== undefined && zoom > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
      window.google.maps.event.removeListener(listener);
    });
    // `markers` is deliberately not a dependency: refitting on every dropped pin
    // would yank the viewport out from under the rep.
  }, [fitKey, ready]);

  const retry = useCallback(() => {
    setError(null);
    setRetryToken((token) => token + 1);
  }, []);

  return (
    <Box sx={{ position: 'relative', height, width: '100%' }}>
      <Box
        ref={containerRef}
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'sm',
          overflow: 'hidden',
          cursor: ready && pinningEnabled ? 'crosshair' : undefined,
        }}
      />

      {!ready && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            backgroundColor: 'background.surface',
            borderRadius: 'sm',
          }}
        >
          <CircularProgress />
          <Typography level="body-sm">Loading satellite map…</Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            backgroundColor: 'background.surface',
            borderRadius: 'sm',
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: 460 }}>
            <Alert color="danger" variant="soft">
              <Box>
                <Typography level="title-sm">The map could not start</Typography>
                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                  {error}
                </Typography>
              </Box>
            </Alert>
            <Button variant="outlined" color="neutral" onClick={retry} sx={{ alignSelf: 'flex-start' }}>
              Try again
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};
